import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@agora/agora-common/Button';
import { withStyles, withTheme } from '@agora/agora-common';
import TextField from '@material-ui/core/TextField';
import TinyLoadingIndicator from '../../Common/TinyLoadingIndicator/TinyLoadingIndicator';
import FormSection from '../Common/FormSection';

import {
  ResponseError,
  createPubkey,
  expirePubkey,
  getBiscuit,
  getPubkeys,
} from '../../../utils/api';

const styles = theme => ({
  textField: {
    margin: '1em 0',
    '& textarea': {
      color: theme.palette.primary.contrastText,
    },
  },
  anchor: {
    color: theme.palette.secondary.main,
  },
  pubkey: {
    color: theme.palette.primary.main,
    margin: '1em 0',
    padding: '0.5em',
    '& .meta': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      fontSize: 'small',
      margin: '0 1em',
    },
  },
  section: {
    margin: '1em 0',
  },
});

/**
 * @param {(_: string) => void} setError - Set the error message
 */
const fetchBiscuit = (setError, setExpiry) =>
  getBiscuit().then(
    /* eslint-disable camelcase */
    ({ access_token, expires_in }) => {
      const expiry = new Date(Date.now() + 1000 * expires_in);
      setExpiry(expiry);
      const a = document.createElement('a');
      const url = URL.createObjectURL(
        new Blob([access_token], { type: 'text/plain' }),
      );
      a.href = url;
      a.download = `agora_biscuit_${expiry.toISOString()}.txt`;
      a.click();
      return Promise.resolve().finally(() => {
        URL.revokeObjectURL(url);
        a.remove();
      });
    },
    err => {
      if (err instanceof ResponseError) {
        console.error(err.statusText || err.response.status);
        err.response.status === 401
          && setError('Cannot generate a biscuit — try logging out and in again.');
        return Promise.resolve(err);
      }
      return Promise.reject(err);
    },
  );

const fetchPubkeys = setPubkeys =>
  getPubkeys().then(pubkeys =>
    setPubkeys(
      pubkeys.map(({ pubkey, created, pubkey_id }) => ({
        pubkey,
        pubkey_id,
        created,
      })),
    ));

const handlePubkeySubmit = ({ pubkey, setPubkey, setPubkeys, setIsSaving }) =>
  e => {
    e.preventDefault();

    if (pubkey.match(/^ed25519\/[0-9a-f]{64}$/)) {
      setIsSaving(true);
      createPubkey({ pubkey })
        .then(
          // eslint-disable-next-line no-sequences
          pk => (setPubkeys(pks => [...pks, { pubkey, ...pk }]), setPubkey('')),
        )
        .finally(() => setIsSaving(false));
    }
  };

function Biscuit({ classes }) {
  const [isSaving, setIsSaving] = useState(false);
  const [expiry, setExpiry] = useState(null);
  const [error, setError] = useState('');
  const [pubkey, setPubkey] = useState('');
  const [pubkeys, setPubkeys] = useState([]);

  useEffect(() => {
    fetchPubkeys(setPubkeys);
  }, []);

  return (
    <FormSection title="API access">
      <div className={classes.section}>
        <p>
          API access uses{' '}
          <a className={classes.anchor} href="https://www.biscuitsec.org/">
            Biscuit tokens
          </a>
          . There is a command line client that works with abritrary programming
          languages. The client handles signing and sending of requests.
          Alternatively, one can sign and send requests manually. The client
          executable can be downloaded from the links below. See
          <a
            className={classes.anchor}
            href="https://www.crucialab.net/post/agora-trading-guide/"
          >
            AGORA Trading Guide | CRUCIAL
          </a>
          .
        </p>
        <ul>
          <li>
            <a href="https://dl.crucialab.net/agora-client/linux/latest">
              Linux (x86_64, musl)
            </a>
          </li>
          <li>
            <a href="https://dl.crucialab.net/agora-client/macos/latest">
              MacOS (aarch64)
            </a>
          </li>
          <li>
            <a href="https://dl.crucialab.net/agora-client/windows/latest">
              Windows (x86_64)
            </a>
          </li>
        </ul>
        <p>
          Whether using the client or manual methods, each account holder should
          download a biscuit file from the link on this page.{' '}
        </p>
        <ol>
          <li>
            Click the button below to download the biscuit associated with this
            login session (the authority biscuit).
          </li>
          <li>
            Use{' '}
            <code>agora-client --biscuit-file $BISCUIT_FILE pubkey new</code> to
            generate a public and private keypair, and display the public key.
          </li>
          <li>
            Enter this public key in the text box below, and click to add it.
          </li>
          <li>
            Use <code>agora-client --biscuit-file $BISCUIT_FILE get me</code> to
            confirm everything is working, then use the{' '}
            <a
              className={classes.anchor}
              href="https://www.crucialab.net/post/agora-trading-guide/"
            >
              AGORA Trading Guide
            </a>{' '}
            to see the functionality that is available.
          </li>
        </ol>
        <details>
          <summary>Manual requests</summary>
          <p>Sending requests manually is more complex. In brief,</p>
          <ol>
            <li>
              Use <code>biscuit-cli keypair</code> to generate a public and
              private keypair (ed25519 — the default).
            </li>
            <li>
              Enter the <em>public</em> key component in the text box below, and
              click to add it.
            </li>
            <li>
              Click the button to download the biscuit associated with this
              login session (the authority biscuit).
            </li>
          </ol>
          <p>
            The client library uses a private key you control and the biscuit
            you have received. It creates a new biscuit, specific to that API
            request, and includes it with the request as a bearer token. To do
            this manually,
          </p>
          <ol>
            <li>
              Create a third party block file that includes the{' '}
              <code>operation</code> (read/write), the <code>pathname</code>,
              and a current v7 UUID as <code>nonce</code>. (Note: write requests
              will require a sha256 hash of the request JSON, too — this is not
              yet supported.)
            </li>
            <li>
              Create the third party block request from the authority biscuit.
            </li>
            <li>
              Generate the third party block itself with the private key and the
              block file.
            </li>
            <li>
              Append this third party block to the authority biscuit to create
              an attenuated biscuit.
            </li>
            <li>
              Pass this attenuated biscuit as an{' '}
              <code>Authorization: Bearer</code> token, with the request.
            </li>
          </ol>
        </details>

        <p>
          While we begin allowing API access, the authority biscuits will have a
          short lifetime (a little over a week). We will likely extend this,
          once the API is stable.
        </p>
      </div>
      <div className={classes.section}>
        <Button
          color="primary"
          disabled={isSaving || expiry != null || error !== ''}
          type="button"
          onClick={e => {
            e.preventDefault();

            setIsSaving(true);
            setError('');
            fetchBiscuit(setError, setExpiry).finally(() => setIsSaving(false));
          }}
          variant="contained"
        >
          <div>
            {isSaving ? (
              <TinyLoadingIndicator message="Please wait..." />
            ) : (
              'Download biscuit'
            )}
          </div>
        </Button>
        {error === '' || <p>{error}</p>}
        {
          /* eslint-disable eqeqeq */
          expiry == null || <p>Expires: {expiry.toString()}</p>
        }
      </div>
      <div className={classes.section}>
        <p>
          Enter public key as{' '}
          <code>
            {
              /* eslint-disable no-template-curly-in-string */
              'ed25519/${HEX_ENCODED_KEY?}'
            }
          </code>
          :
        </p>
        <TextField
          defaultValue=""
          disabled={isSaving}
          fullWidth
          multiline
          onChange={event => setPubkey(event.target.value.trim())}
          error={!!error}
          helperText={error}
          classes={{ root: classes.textField }}
        />
        <Button
          color="primary"
          disabled={!pubkey.match(/^ed25519\/[0-9a-f]{64}$/) || isSaving}
          type="button"
          onClick={handlePubkeySubmit({
            pubkey,
            setPubkey,
            setPubkeys,
            setIsSaving,
          })}
          variant="contained"
        >
          <div>
            {isSaving || 'Add public key'}
            {isSaving && <TinyLoadingIndicator message="Please wait..." />}
          </div>
        </Button>
      </div>
      <div className={classes.section}>
        {pubkeys.map(({ pubkey: pk, pubkey_id, created }) => (
          <div key={pubkey_id} className={classes.pubkey}>
            <code>{pk}</code>
            <div className="meta">
              <p>Created {created}</p>
              <Button
                color="danger"
                type="button"
                onClick={e => {
                  e.preventDefault();
                  expirePubkey(pk).then(() =>
                    setPubkeys(pks => pks.filter(({ pubkey: p }) => p !== pk)));
                }}
                variant="contained"
              >
                <div>
                  {isSaving || 'Expire key'}
                  {isSaving && (
                    <TinyLoadingIndicator message="Please wait..." />
                  )}
                </div>
              </Button>
            </div>
          </div>
        ))}
      </div>
    </FormSection>
  );
}

Biscuit.propTypes = {
  classes: PropTypes.object.isRequired,
};

Biscuit.defaultProps = {};

export default withStyles(styles)(withTheme(Biscuit));
